import { use } from 'react';
import { useTranslation } from 'react-i18next';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import moment from 'moment';
import SearchBar from 'shared/components/search-bar/search-bar';
import FilterChipDateTimeRange from 'shared/components/table-filter-chips/date-time-range-filter-chip/filter-chip-date-time-range';
import FilterChip from 'shared/components/table-filter-chips/filter-chip/filter-chip';
import MultiValueFilterChip from 'shared/components/table-filter-chips/multi-value-filter-chip/multi-value-filter-chip';
import MultiValueSearchFilterChip from 'shared/components/table-filter-chips/multi-value-search-filter-chip/multi-value-search-filter-chip';
import OrganisationUnitFilterChip from 'shared/components/table-filter-chips/organisation-unit-filter-chip/organisation-unit-filter-chip';
import { OrganisationUnitTreeProvider } from 'shared/features/organisation/providers/organisation-unit-tree-provider';

import { ResidentsChangeLogFilterValueType } from 'features/residents/models/read-residents-change-log-filter-value';
import { ResidentChangeLogCategory } from 'features/residents/models/resident-change-log-category';
import ResidentsChangeLogProviderContext from 'features/residents/resident-change-logs/providers/contexts/residents-change-log-provider-context';
import styles from './residents-change-log-filters.module.scss';

const ResidentsChangeLogFilters = () => {
  const { t } = useTranslation('residentsChangeLog');

  const {
    filtersState: {
      clearFilters,
      changeSearchBarValue,
      searchBar,
      changeSelectedOrganisationUnits,
      organisationUnits,
      changeFilters,
      categoryOptions,
      selectedCategoryOptions,
      scenarioOptions,
      selectedScenarioOptions,
      typeOptions,
      selectedTypeOptions,
      setDateFilters,
      employeeOptions,
      deviceOptions,
      selectedDeviceOptions,
      selectedEmployeeOptions,
      muteScenariosOptions,
      selectedMuteScenariosOption,
      dateFilter,
    },
    refresh,
  } = use(ResidentsChangeLogProviderContext);

  return (
    <div className={styles.container}>
      <div className={styles.filters}>
        <SearchBar onSearchValueChanged={changeSearchBarValue} searchValue={searchBar} />
        <OrganisationUnitTreeProvider.AccessRightsOnly excludeBranchesWithoutResidents>
          <OrganisationUnitFilterChip
            onOptionsSelected={changeSelectedOrganisationUnits}
            selectedOptions={organisationUnits}
            placeHolder={t('filters.locationLabel')}
          />
        </OrganisationUnitTreeProvider.AccessRightsOnly>
        <MultiValueFilterChip
          key={`filter-chip-category`}
          options={categoryOptions}
          placeHolder={t('filters.categoryLabel')}
          onOptionsSelected={(keys: string[]) => changeFilters(ResidentsChangeLogFilterValueType.Category, keys)}
          selectedOptions={selectedCategoryOptions}
        />
        {selectedCategoryOptions.map((option) => {
          switch (option.key) {
            case ResidentChangeLogCategory.Scenario:
              return (
                <MultiValueFilterChip
                  key={`filter-chip-scenario`}
                  options={scenarioOptions}
                  placeHolder={t('filters.scenarioLabel')}
                  onOptionsSelected={(keys: string[]) => changeFilters(ResidentsChangeLogFilterValueType.Scenario, keys)}
                  selectedOptions={selectedScenarioOptions}
                />
              );
            case ResidentChangeLogCategory.Device:
              return (
                <MultiValueSearchFilterChip
                  key={`filter-chip-device`}
                  options={deviceOptions}
                  placeHolder={t('filters.deviceLabel')}
                  onOptionsSelected={(keys: string[]) => changeFilters(ResidentsChangeLogFilterValueType.Device, keys)}
                  selectedOptions={selectedDeviceOptions}
                />
              );
            case ResidentChangeLogCategory.MuteScenarios:
              return (
                <FilterChip
                  key={`filter-chip-mute-scenarios`}
                  options={muteScenariosOptions}
                  placeHolder={t('filters.muteScenariosLabel')}
                  onOptionSelected={(key) => changeFilters(ResidentsChangeLogFilterValueType.MuteScenarios, key ? [key] : [])}
                  initialSelectedOption={selectedMuteScenariosOption}
                />
              );
            default:
              return <></>;
          }
        })}
        <MultiValueSearchFilterChip
          key={`filter-chip-type`}
          options={typeOptions}
          placeHolder={t('filters.changeLabel')}
          onOptionsSelected={(keys: string[]) => changeFilters(ResidentsChangeLogFilterValueType.Change, keys)}
          selectedOptions={selectedTypeOptions}
        />
        <FilterChipDateTimeRange
          key={'filter-chip-date-time'}
          placeHolder={t('filters.pointInTimeLabel')}
          timeRange={30}
          minimumStartDate={moment().subtract(1, 'years')}
          initialDateFilters={dateFilter}
          onDateFiltersChanged={setDateFilters}
        />
        <MultiValueSearchFilterChip
          key={`filter-chip-employee`}
          options={employeeOptions}
          placeHolder={t('filters.employeeLabel')}
          onOptionsSelected={(keys: string[]) => changeFilters(ResidentsChangeLogFilterValueType.Employee, keys)}
          selectedOptions={selectedEmployeeOptions}
        />
        <div className={styles.clearFilters}>
          <Button variant="text" color="primary" onClick={clearFilters}>
            {t('filters.clearFilters')}
          </Button>
        </div>
      </div>
      <IconButton className={styles.refreshButton} onClick={refresh}>
        <RefreshRounded />
      </IconButton>
    </div>
  );
};

export default ResidentsChangeLogFilters;
